import React, { useId } from "react"
import { Alert, Checkbox, Input, InputNumber, Radio, Tag } from "antd"
import {
  SurveyAnswers,
  SurveyAnswerValue,
  SurveyCard,
  SurveyCell,
  SurveyCellError,
  SurveyOption,
} from "types/surveyTest.types"
import { openOf } from "../model/session"

/** A row or column of a card. */
type Item = { id: string; text: string }

interface Props {
  card: SurveyCard
  values: SurveyAnswers
  errors: SurveyCellError[]
  onChange: (cell: string, value: SurveyAnswerValue | undefined) => void
  /** Enter in a number box or a one-line input: the drawer moves on, as Next does. */
  onSubmit?: () => void
}

/** What the answer controls of a card need. */
interface AreaProps {
  card: SurveyCard
  values: SurveyAnswers
  errorsAt: (cell: string) => SurveyCellError[]
  onChange: Props["onChange"]
  onSubmit?: () => void
  /** The id of the question text, which names a plain question's controls and a grid's table */
  labelId: string
  /** The id of a list's or a grid's range hint, shown once above it */
  hintId?: string
}

/** What a control needs to show and change one cell. */
interface CellProps extends AreaProps {
  cell: SurveyCell
}

/** A multi-select's next value when option `id` is ticked or unticked, keeping exclusive options alone. */
export const toggleMulti = (
  value: string[],
  id: string,
  on: boolean,
  options: SurveyOption[]
): string[] | undefined => {
  const exclusive = new Set(options.filter((o) => o.exclusive).map((o) => o.id))
  let next = on
    ? exclusive.has(id)
      ? [id]
      : [...value.filter((v) => !exclusive.has(v)), id]
    : value.filter((v) => v !== id)
  next = options.map((o) => o.id).filter((o) => next.includes(o))
  return next.length ? next : undefined
}

/** An option the card does not describe: it shows as its id. */
const unknownOption = (id: string): SurveyOption => ({
  id,
  text: id,
  exception: false,
  exclusive: false,
  open: false,
  terminates: false,
})

/** A row or column the card does not describe: it shows as its id. */
const unknownItem = (id: string): Item => ({ id, text: id })

/** The text an option, row or column shows; an empty text shows the id. */
const textOf = (x: Item): string => x.text || x.id

/** The cell's shown options in shown order; an id the card does not describe shows as itself. */
const shownOptions = (card: SurveyCard, cell: SurveyCell): SurveyOption[] => {
  const byId = new Map(card.options.map((o) => [o.id, o]))
  return cell.shown.map((id) => byId.get(id) ?? unknownOption(id))
}

/** The items whose ids the cells use, in the order `list` gives, then any used id it does not describe. */
const inOrder = <T extends { id: string }>(list: T[], used: string[], make: (id: string) => T): T[] => {
  const want = new Set(used)
  const known = list.filter((x) => want.has(x.id))
  const have = new Set(known.map((x) => x.id))
  return [...known, ...[...want].filter((id) => !have.has(id)).map(make)]
}

/** The text of a cell's grid row. */
const rowTextOf = (card: SurveyCard, cell: SurveyCell): string =>
  textOf(card.rows.find((r) => r.id === cell.row) ?? unknownItem(cell.row))

/** The option ids a cell's value chooses: a single's id, or a multi's ids. */
const chosenIds = (card: SurveyCard, v: SurveyAnswerValue | undefined): string[] =>
  card.kind === "single" && typeof v === "string" ? [v] : card.kind === "multi" && Array.isArray(v) ? v : []

/** The specify inputs a cell shows: one per option chosen now that asks to specify, in shown order. */
const specifyOf = (card: SurveyCard, cell: SurveyCell, values: SurveyAnswers): { option: SurveyOption; id: string }[] => {
  const chosen = chosenIds(card, values[cell.id])
  return shownOptions(card, cell).flatMap((option) => {
    const id = chosen.includes(option.id) ? openOf(cell, option.id) : undefined
    return id === undefined ? [] : [{ option, id }]
  })
}

/** The cell ids whose errors show at a control: the cell, and the specify input of each option chosen now. */
const placedCells = (card: SurveyCard, cell: SurveyCell, values: SurveyAnswers): string[] => [
  cell.id,
  ...specifyOf(card, cell, values).map((s) => s.id),
]

/** A control's errors, in red; the control points at `id` to be described by them. A grid leads them with its column. */
const ErrorLine: React.FC<{ id: string; errors: SurveyCellError[]; lead?: string }> = ({ id, errors, lead }) =>
  errors.length ? (
    <div id={id} className="mt-1 space-y-1">
      {errors.map((e, i) => (
        <div key={i} role="alert" className="text-red-600 text-sm">
          {lead && <span className="font-medium">{lead}: </span>}
          {e.message}
        </div>
      ))}
    </div>
  ) : null

/** A class list from the names given, leaving out the ones that do not apply. */
export const classes = (...names: (string | false | undefined)[]): string => names.filter(Boolean).join(" ")

/** The card a question, or the end of the survey, is drawn in. */
export const CARD = "mx-auto w-full max-w-[720px] rounded-lg border border-gray-200 bg-white shadow-sm p-6"

/** Errors as "cell: message" lines, for an alert; an error without a cell shows its message only. */
export const ErrorList: React.FC<{ errors: SurveyCellError[] }> = ({ errors }) => (
  <div className="flex flex-col gap-0.5 break-words">
    {errors.map((e, i) => (
      <div key={i}>
        {e.cell && <span className="font-medium">{e.cell}: </span>}
        {e.message}
      </div>
    ))}
  </div>
)

/** The tag on an option that ends the survey: a danger red. */
const ENDS_TAG = "me-0 border-red-200 bg-red-50 text-red-600"

/** An `aria-describedby` over the ids given, or undefined when there are none. */
const describedBy = (...ids: (string | false | undefined)[]): string | undefined =>
  ids.filter(Boolean).join(" ") || undefined

/**
 * What the tester should know about the card: that a hidden question's value is theirs to set, then the
 * engine's notes on what shows or is checked differently from the live survey.
 */
const Notes: React.FC<{ card: SurveyCard }> = ({ card }) => {
  const notes = [
    ...(card.elem === "hidden" || card.elem === "data"
      ? ["Hidden question: the survey platform or the sample sets this value. Set it to test the routing."]
      : []),
    ...card.notes.map((n) => n.message),
  ]
  if (!notes.length) return null
  return (
    <div className="mt-3 space-y-2">
      {notes.map((n, i) => (
        <Alert key={i} type="info" showIcon className="py-1.5 text-xs" message={n} />
      ))}
    </div>
  )
}

/** Enter in a one-line input moves on; Enter that ends an IME composition does not. */
const enterHandler = (onSubmit?: () => void) =>
  onSubmit &&
  ((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!e.nativeEvent.isComposing) onSubmit()
  })

/** The input for the text of a chosen open option, at its own cell `id`; its errors show where the caller puts `errorId`. */
const SpecifyInput: React.FC<{
  id: string
  name: string
  values: SurveyAnswers
  invalid: boolean
  errorId: string
  onChange: Props["onChange"]
  onSubmit?: () => void
}> = ({ id, name, values, invalid, errorId, onChange, onSubmit }) => {
  const typed = values[id]
  return (
    <Input
      aria-label={name}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy(invalid && errorId)}
      value={typeof typed === "string" ? typed : ""}
      status={invalid ? "error" : undefined}
      onChange={(e) => onChange(id, e.target.value || undefined)}
      onPressEnter={enterHandler(onSubmit)}
    />
  )
}

/**
 * One option as a full-width row: antd's own wrapper label is the row, so a click anywhere on it
 * chooses the option. A terminate option is tagged on the right, and a chosen option that asks to
 * specify gets its input under the row, outside the label.
 */
const OptionRow: React.FC<
  CellProps & { option: SurveyOption; options: SurveyOption[]; chosen: string[]; listError: boolean }
> = ({ card, cell, values, errorsAt, onChange, onSubmit, option, options, chosen, listError }) => {
  const errorId = useId()
  const on = chosen.includes(option.id)
  const open = on ? openOf(cell, option.id) : undefined
  const openErrors = open !== undefined ? errorsAt(open) : []
  // antd spaces the control and the text by padding its label span; that span grows to the right edge.
  // antd centres the control on the text; on a wrapped text it belongs on the first 22px line.
  const row = [
    "flex items-start rounded-md border px-3 py-2.5 cursor-pointer transition-colors me-0",
    "[&>.ant-radio-label]:flex-1 [&>.ant-checkbox-label]:flex-1",
    "[&>.ant-radio-label]:min-w-0 [&>.ant-checkbox-label]:min-w-0",
    "[&>.ant-radio]:self-start [&>.ant-radio]:mt-[3px] [&>.ant-checkbox]:self-start [&>.ant-checkbox]:mt-[3px]",
    listError ? "border-red-400" : on ? "border-[var(--dq-brand)]" : "border-gray-200",
    on ? "bg-[var(--dq-brand-soft)]" : "hover:bg-gray-50",
  ].join(" ")
  const body = (
    <span className="flex items-start justify-between gap-3">
      <span className="min-w-0 break-words text-gray-800">{option.text}</span>
      {option.terminates && <Tag className={`${ENDS_TAG} shrink-0`}>Ends the survey</Tag>}
    </span>
  )
  return (
    <div>
      {card.kind === "single" ? (
        <Radio value={option.id} className={row}>
          {body}
        </Radio>
      ) : (
        <Checkbox
          className={row}
          checked={on}
          aria-invalid={listError || undefined}
          onChange={(e) => onChange(cell.id, toggleMulti(chosen, option.id, e.target.checked, options))}
        >
          {body}
        </Checkbox>
      )}
      {open !== undefined && (
        <div className="ml-8 mt-2">
          <SpecifyInput
            id={open}
            name={`${option.text}: specify`}
            values={values}
            invalid={openErrors.length > 0}
            errorId={errorId}
            onChange={onChange}
            onSubmit={onSubmit}
          />
          <ErrorLine id={errorId} errors={openErrors} />
        </div>
      )}
    </div>
  )
}

/** A single or multi cell: its shown options as rows, exceptions after a divider, named by the question text. */
const ChoiceList: React.FC<CellProps> = (props) => {
  const { card, cell, values, errorsAt, onChange, labelId } = props
  const errorId = useId()
  const single = card.kind === "single"
  const options = shownOptions(card, cell)
  const chosen = chosenIds(card, values[cell.id])
  const listErrors = errorsAt(cell.id)
  const rows = (list: SurveyOption[]) =>
    list.map((o) => (
      <OptionRow key={o.id} {...props} option={o} options={options} chosen={chosen} listError={listErrors.length > 0} />
    ))
  const normal = options.filter((o) => !o.exception)
  const exceptions = options.filter((o) => o.exception)
  const list = (
    <div
      role={single ? "radiogroup" : "group"}
      aria-labelledby={labelId}
      aria-invalid={(single && listErrors.length > 0) || undefined}
      aria-describedby={describedBy(listErrors.length > 0 && errorId)}
    >
      <div className="space-y-2">{rows(normal)}</div>
      {exceptions.length > 0 && (
        <div className={normal.length ? "border-t border-gray-100 pt-2 mt-2 space-y-2" : "space-y-2"}>
          {rows(exceptions)}
        </div>
      )}
    </div>
  )
  return (
    <>
      {single ? (
        <Radio.Group className="block w-full" value={chosen[0] ?? null} onChange={(e) => onChange(cell.id, e.target.value)}>
          {list}
        </Radio.Group>
      ) : (
        list
      )}
      <ErrorLine id={errorId} errors={listErrors} />
    </>
  )
}

/** How a control is named: by the id of a text on screen, or by a text of its own. */
type ControlName = { "aria-labelledby": string } | { "aria-label": string }

/** A number or real card shows its range as a hint when it has a bound; the engine checks it. */
const hasRangeHint = (card: SurveyCard): boolean =>
  (card.kind === "number" || card.kind === "real") && (card.rules.min !== null || card.rules.max !== null)

/** "Between 18 and 99", or one bound alone: "At least 18", "At most 99". */
const rangeHint = ({ min, max }: SurveyCard["rules"]): string =>
  min !== null && max !== null ? `Between ${min} and ${max}` : min !== null ? `At least ${min}` : `At most ${max}`

const RangeHint: React.FC<{ id: string; card: SurveyCard; className?: string }> = ({ id, card, className }) => (
  <div id={id} className={classes("text-xs text-gray-500", className)}>
    {rangeHint(card.rules)}
  </div>
)

/**
 * A number, real or text box for one cell. A number keeps what was typed, out of range or with
 * decimals, so the engine's checks see it. The caller names the box and shows its hint and errors.
 */
const FillIn: React.FC<{
  card: SurveyCard
  cell: SurveyCell
  values: SurveyAnswers
  onChange: Props["onChange"]
  onSubmit?: () => void
  name: ControlName
  invalid: boolean
  /** The ids of the hint and the error line that describe the box */
  description?: string
  /** In a list or a grid: the normal size, and a text box that starts at one line */
  compact?: boolean
}> = ({ card, cell, values, onChange, onSubmit, name, invalid, description, compact = false }) => {
  const v = values[cell.id]
  const status = invalid ? "error" : undefined
  const size = compact ? "middle" : "large"
  if (card.kind === "text")
    return (
      <Input.TextArea
        {...name}
        aria-invalid={invalid || undefined}
        aria-describedby={description}
        size={size}
        autoSize={{ minRows: compact ? 1 : 3 }}
        value={typeof v === "string" ? v : ""}
        status={status}
        onChange={(e) => onChange(cell.id, e.target.value || undefined)}
      />
    )
  return (
    <InputNumber
      {...name}
      aria-invalid={invalid || undefined}
      aria-describedby={description}
      size={size}
      className="w-full"
      value={typeof v === "number" ? v : null}
      status={status}
      onChange={(n) => onChange(cell.id, typeof n === "number" ? n : undefined)}
      onPressEnter={enterHandler(onSubmit)}
    />
  )
}

/** A plain number, real or text question: one full-width box named by the question text, the range under it. */
const PlainFillIn: React.FC<CellProps> = ({ card, cell, values, errorsAt, onChange, onSubmit, labelId }) => {
  const hintId = useId()
  const errorId = useId()
  const errors = errorsAt(cell.id)
  const invalid = errors.length > 0
  const hint = hasRangeHint(card)
  return (
    <>
      <FillIn
        card={card}
        cell={cell}
        values={values}
        onChange={onChange}
        onSubmit={onSubmit}
        name={{ "aria-labelledby": labelId }}
        invalid={invalid}
        description={describedBy(hint && hintId, invalid && errorId)}
      />
      {hint && <RangeHint id={hintId} card={card} className="mt-1" />}
      <ErrorLine id={errorId} errors={errors} />
    </>
  )
}

/** One line of a list: the text on the left, the control on the right, its errors under the control. */
const Line: React.FC<{ text: string; invalid: boolean; errors: React.ReactNode; children: React.ReactNode }> = ({
  text,
  invalid,
  errors,
  children,
}) => (
  <div
    className={classes(
      "grid grid-cols-[1fr_minmax(160px,40%)] gap-x-3 items-center rounded-md px-3 py-1.5",
      invalid && "bg-red-50"
    )}
  >
    <div className="min-w-0 break-words text-gray-800">{text}</div>
    {children}
    {invalid && <div className="col-start-2">{errors}</div>}
  </div>
)

/** A LIST FILL IN row: its text, and a box named by it. */
const FillInLine: React.FC<CellProps> = ({ card, cell, values, errorsAt, onChange, onSubmit, hintId }) => {
  const errorId = useId()
  const errors = errorsAt(cell.id)
  const invalid = errors.length > 0
  const text = rowTextOf(card, cell)
  return (
    <Line text={text} invalid={invalid} errors={<ErrorLine id={errorId} errors={errors} />}>
      <FillIn
        card={card}
        cell={cell}
        values={values}
        onChange={onChange}
        onSubmit={onSubmit}
        name={{ "aria-label": text }}
        invalid={invalid}
        description={describedBy(hintId, invalid && errorId)}
        compact
      />
    </Line>
  )
}

/** A LIST FILL IN: one line per row, as a group named by the question text. */
const FillInList: React.FC<AreaProps> = (props) => (
  <div role="group" aria-labelledby={props.labelId} className="space-y-1">
    {props.card.cells.map((cell) => (
      <FillInLine key={cell.id} {...props} cell={cell} />
    ))}
  </div>
)

/** The specify input of one grid row's open option, as a line under the table. */
const SpecifyLine: React.FC<AreaProps & { id: string; text: string }> = ({
  values,
  errorsAt,
  onChange,
  onSubmit,
  id,
  text,
}) => {
  const errorId = useId()
  const errors = errorsAt(id)
  const invalid = errors.length > 0
  return (
    <Line text={text} invalid={invalid} errors={<ErrorLine id={errorId} errors={errors} />}>
      <SpecifyInput
        id={id}
        name={`${text}: specify`}
        values={values}
        invalid={invalid}
        errorId={errorId}
        onChange={onChange}
        onSubmit={onSubmit}
      />
    </Line>
  )
}

/**
 * A grid's table, named by the question text: it scrolls sideways inside the card, under a header of
 * centred texts. A column whose option ends the survey is tagged, as a plain option row is.
 */
const GridTable: React.FC<{
  labelId: string
  head: (Item & { terminates?: boolean })[]
  children: React.ReactNode
}> = ({ labelId, head, children }) => (
  <div className="overflow-x-auto">
    <table aria-labelledby={labelId} className="w-full text-sm">
      <thead>
        <tr>
          <td />
          {head.map((h) => (
            <th
              key={h.id}
              scope="col"
              className="px-2 pb-2 text-center align-bottom text-xs font-medium text-gray-600"
            >
              {textOf(h)}
              {h.terminates && (
                <div className="mt-1">
                  <Tag className={ENDS_TAG}>Ends the survey</Tag>
                </div>
              )}
            </th>
          ))}
        </tr>
      </thead>
      {children}
    </table>
  </div>
)

/**
 * A grid row as a row group of its own: the row's text and cells, then its errors in a table row under it.
 * Table cells lead with a plain class: jsdom's selector engine builds selectors from a cell's first class
 * and fails on one with "." or "[".
 */
const TableRow: React.FC<{
  text: string
  striped: boolean
  invalid: boolean
  span: number
  errors: React.ReactNode
  children: React.ReactNode
}> = ({ text, striped, invalid, span, errors, children }) => (
  <tbody className={invalid ? "bg-red-50" : striped ? "bg-gray-50" : undefined}>
    <tr>
      <th scope="row" className="text-left py-2.5 pl-3 pr-4 font-normal text-gray-800">
        {text}
      </th>
      {children}
    </tr>
    {invalid && (
      <tr>
        <td colSpan={span} className="px-3 pb-2">
          {errors}
        </td>
      </tr>
    )}
  </tbody>
)

/**
 * A radio or checkbox in a grid cell: antd's own wrapper label fills the cell, so a click anywhere in it
 * chooses. The cell sets `h-px` so that the label's full height resolves to the row's height.
 */
const CELL_CONTROL =
  "flex h-full w-full items-center justify-center me-0 px-3 py-2.5 cursor-pointer transition-colors hover:bg-[color-mix(in_srgb,var(--dq-brand)_5%,transparent)]"

/** One row of a choice grid: its text, then a control in each column its cell shows; the others stay empty. */
const ChoiceTableRow: React.FC<CellProps & { columns: SurveyOption[]; striped: boolean }> = ({
  card,
  cell,
  values,
  errorsAt,
  onChange,
  columns,
  striped,
}) => {
  const name = useId()
  const errorId = useId()
  const errors = errorsAt(cell.id)
  const invalid = errors.length > 0
  const options = shownOptions(card, cell)
  const chosen = chosenIds(card, values[cell.id])
  const rowText = rowTextOf(card, cell)
  return (
    <TableRow
      text={rowText}
      striped={striped}
      invalid={invalid}
      span={columns.length + 1}
      errors={<ErrorLine id={errorId} errors={errors} />}
    >
      {columns.map((o) => {
        if (!cell.shown.includes(o.id)) return <td key={o.id} />
        const on = chosen.includes(o.id)
        const label = `${rowText}: ${textOf(o)}${o.terminates ? " (ends the survey)" : ""}`
        const description = describedBy(invalid && errorId)
        return (
          <td key={o.id} className={classes("h-px p-0", on && "bg-[var(--dq-brand-soft)]")}>
            {card.kind === "single" ? (
              <Radio
                className={CELL_CONTROL}
                name={name}
                value={o.id}
                checked={on}
                aria-label={label}
                aria-invalid={invalid || undefined}
                aria-describedby={description}
                onChange={() => onChange(cell.id, o.id)}
              />
            ) : (
              <Checkbox
                className={CELL_CONTROL}
                checked={on}
                aria-label={label}
                aria-invalid={invalid || undefined}
                aria-describedby={description}
                onChange={(e) => onChange(cell.id, toggleMulti(chosen, o.id, e.target.checked, options))}
              />
            )}
          </td>
        )
      })}
    </TableRow>
  )
}

/**
 * A grid of singles or multis: one table row per cell, one column per option that any row shows,
 * and under the table a specify input for each chosen option that asks for one.
 */
const ChoiceTable: React.FC<AreaProps> = (props) => {
  const { card, values, labelId } = props
  const columns = inOrder(
    card.options,
    card.cells.flatMap((c) => c.shown),
    unknownOption
  )
  const specify = card.cells.flatMap((cell) =>
    specifyOf(card, cell, values).map(({ option, id }) => ({
      id,
      text: `${rowTextOf(card, cell)}, ${textOf(option)}`,
    }))
  )
  return (
    <>
      <GridTable labelId={labelId} head={columns}>
        {card.cells.map((cell, i) => (
          <ChoiceTableRow key={cell.id} {...props} cell={cell} columns={columns} striped={i % 2 === 0} />
        ))}
      </GridTable>
      {specify.length > 0 && (
        <div className="mt-3 space-y-1">
          {specify.map((s) => (
            <SpecifyLine key={s.id} {...props} id={s.id} text={s.text} />
          ))}
        </div>
      )}
    </>
  )
}

/** The key of a fill-in grid's cell by its row and column ids. */
const gridKey = (row: string, column: string): string => `${row}\u0000${column}`

/** One row of a fill-in grid: its text, a box in each column it has a cell for, and each box's errors under it. */
const FillInGridRow: React.FC<
  AreaProps & {
    row: Item
    columns: Item[]
    /** The grid's cells by `gridKey` */
    cellAt: Map<string, SurveyCell>
    striped: boolean
  }
> = ({ card, values, errorsAt, onChange, onSubmit, hintId, row, columns, cellAt, striped }) => {
  const errorBase = useId()
  const rowText = textOf(row)
  const boxes = columns.map((col, i) => {
    const cell = cellAt.get(gridKey(row.id, col.id))
    return { col, cell, errors: cell ? errorsAt(cell.id) : [], errorId: `${errorBase}${i}` }
  })
  return (
    <TableRow
      text={rowText}
      striped={striped}
      invalid={boxes.some((b) => b.errors.length > 0)}
      span={columns.length + 1}
      errors={boxes.map((b) => (
        <ErrorLine key={b.errorId} id={b.errorId} errors={b.errors} lead={textOf(b.col)} />
      ))}
    >
      {boxes.map(({ col, cell, errors, errorId }) => (
        <td key={col.id} className="px-2 py-1.5 min-w-[6rem]">
          {cell && (
            <FillIn
              card={card}
              cell={cell}
              values={values}
              onChange={onChange}
              onSubmit={onSubmit}
              name={{ "aria-label": `${rowText}, ${textOf(col)}` }}
              invalid={errors.length > 0}
              description={describedBy(hintId, errors.length > 0 && errorId)}
              compact
            />
          )}
        </td>
      ))}
    </TableRow>
  )
}

/** A GRID FILL IN: the columns' texts as header, a row per row text, and a box in each cell. */
const FillInGrid: React.FC<AreaProps> = (props) => {
  const { card, labelId } = props
  const rows = inOrder(
    card.rows,
    card.cells.map((c) => c.row),
    unknownItem
  )
  const columns = inOrder(
    card.columns,
    card.cells.map((c) => c.column),
    unknownItem
  )
  const cellAt = new Map(card.cells.map((c) => [gridKey(c.row, c.column), c]))
  return (
    <GridTable labelId={labelId} head={columns}>
      {rows.map((row, i) => (
        <FillInGridRow key={row.id} {...props} row={row} columns={columns} cellAt={cellAt} striped={i % 2 === 0} />
      ))}
    </GridTable>
  )
}

/**
 * The answer controls: a plain question's one cell, or a grid's rows. Only rows that are cells show, so
 * a row masked by an earlier answer is left out. A grid's notes and range sit once above it. An info card
 * has none.
 */
const Answers: React.FC<AreaProps> = (props) => {
  const hintId = useId()
  const { card } = props
  if (card.kind === "info") return null
  const choice = card.kind === "single" || card.kind === "multi"
  if (card.layout === "plain") {
    const cell = card.cells[0]
    if (!cell) return null
    return choice ? <ChoiceList {...props} cell={cell} /> : <PlainFillIn {...props} cell={cell} />
  }
  const hint = hasRangeHint(card)
  const area = { ...props, hintId: hint ? hintId : undefined }
  return (
    <>
      {(card.rules.unique || hint) && (
        <div className="mb-3 space-y-1">
          {card.rules.unique && <div className="text-sm text-gray-600">Each row needs a different answer.</div>}
          {hint && <RangeHint id={hintId} card={card} />}
        </div>
      )}
      {choice ? <ChoiceTable {...area} /> : card.layout === "grid" ? <FillInGrid {...area} /> : <FillInList {...area} />}
    </>
  )
}

/**
 * One card of the link test, as a tester sees it: one question in one loop context, or an info card, a TEXT
 * element's text with nothing to answer.
 */
const ScreenView: React.FC<Props> = ({ card, values, errors, onChange, onSubmit }) => {
  const labelId = useId()
  const info = card.kind === "info"
  const cells = card.layout === "plain" ? card.cells.slice(0, 1) : card.cells
  const placed = new Set(cells.flatMap((cell) => placedCells(card, cell, values)))
  const elsewhere = errors.filter((e) => !placed.has(e.cell))
  const errorsAt = (cell: string) => errors.filter((e) => e.cell === cell)
  return (
    <div className={CARD}>
      {elsewhere.length > 0 && <Alert type="error" showIcon className="mb-4" message={<ErrorList errors={elsewhere} />} />}
      {card.section && <div className="mb-2 text-xs uppercase tracking-wide text-gray-500">{card.section}</div>}
      {card.loop.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1">
          {card.loop.map((l, i) => (
            <Tag key={i} className="me-0 border-[var(--dq-brand)] bg-[var(--dq-brand-soft)] text-gray-900">
              {`${l.driver}: ${l.text || l.iteration}`}
            </Tag>
          ))}
        </div>
      )}
      <div
        id={labelId}
        className={classes(
          "whitespace-pre-wrap break-words",
          info ? "text-base text-gray-800" : "text-lg font-medium text-gray-900"
        )}
      >
        {card.text || card.question}
      </div>
      <div className="mt-1 text-xs text-gray-400">
        {info ? "Text" : "Question"}: {card.question}
      </div>
      <Notes card={card} />
      {!info && (
        <div className="mt-5">
          <Answers
            card={card}
            values={values}
            errorsAt={errorsAt}
            onChange={onChange}
            onSubmit={onSubmit}
            labelId={labelId}
          />
        </div>
      )}
    </div>
  )
}

export default ScreenView
